import { getFormatDate, getQuestionFromElements } from '@meilleurtaux/form-factory'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import * as yup from 'yup'
import { duplicateObject } from './object'
import type {
  DateConfig,
  OneElementGroup,
  Question as QuestionType,
  Slide,
  Group,
} from '@meilleurtaux/form-factory'
import type { VehicleDefaultValue } from '@meilleurtaux/form-factory-ds'

const emailPattern = /[^@\s]+@[^@\s]+\.[^@\s]+/
const phonePattern = /^0\d\.\d{2}\.\d{2}\.\d{2}\.\d{2}$/

dayjs.extend(customParseFormat)

// ----- Helpers de construction des schémas yup -----

const parseRegex = (pattern: string): RegExp => {
  try {
    return new RegExp(pattern, 'u')
  } catch {
    return new RegExp(pattern)
  }
}

const computeDateMinBound = (rawMin: Date, jour: boolean, mois: boolean): Date => {
  let bornMin = new Date(rawMin)
  if (!jour && !mois) {
    bornMin = new Date(bornMin.getFullYear() - 1, 11, 31)
  } else if (!jour) {
    const month = bornMin.getMonth() - 1
    if (month === -1) {
      bornMin = new Date(bornMin.getFullYear() - 1, 11, 31)
    } else {
      const monthStr = month < 9 ? `0${month + 1}` : (month + 1).toString()
      bornMin = new Date(
        bornMin.getFullYear(),
        month,
        dayjs(`${bornMin.getFullYear()}/${monthStr}`, 'YYYY/MM', true).daysInMonth(),
      )
    }
  }
  bornMin.setHours(0, 0, 0, 0)
  return bornMin
}

const computeDateMaxBound = (rawMax: Date, jour: boolean, mois: boolean): Date => {
  let bornMax = new Date(rawMax)
  if (!jour && !mois) {
    bornMax = new Date(bornMax.getFullYear() + 1, 0, 1)
  } else if (!jour) {
    const month = bornMax.getMonth() + 2
    if (month === 11) {
      bornMax = new Date(bornMax.getFullYear() + 1, 0, 1)
    } else {
      bornMax = new Date(bornMax.getFullYear(), month, 1)
    }
  }
  bornMax.setHours(23, 59, 59, 999)
  return bornMax
}

const buildDateSchema = (question: QuestionType): yup.DateSchema => {
  const config = question.validatorConfig as DateConfig
  const formatDate = getFormatDate(config.jour, config.mois)
  let schema: yup.DateSchema = yup
    .date()
    .transform((_, originalValue) => {
      if (typeof originalValue !== 'string') return originalValue
      if (originalValue === '') return undefined
      const date = dayjs(originalValue.split('|')[0], formatDate, true).add(1, 'hour')
      if (!date.isValid()) return undefined
      return date.toDate()
    })
    .typeError(question.errorMessage)

  if (config.min) {
    const bornMin = computeDateMinBound(config.min, config.jour, config.mois)
    schema = schema.min(bornMin, question.errorMessage)
  }
  if (config.max) {
    const bornMax = computeDateMaxBound(config.max, config.jour, config.mois)
    schema = schema.max(bornMax, question.errorMessage)
  }
  return schema
}

const buildFreeTextFreeSchema = (question: QuestionType): yup.StringSchema => {
  const config = question.validatorConfig as { min: number; max: number; regex: string }
  let schema: yup.StringSchema = yup.string()
  if (config.min !== 0) schema = schema.min(config.min, question.errorMessage)
  if (config.max !== 0) schema = schema.max(config.max, question.errorMessage)
  if (config.regex !== '') schema = schema.matches(parseRegex(config.regex), question.errorMessage)
  return schema
}

const buildFreeTextNumberSchema = (question: QuestionType): yup.NumberSchema => {
  const config = question.validatorConfig as { min: number; max: number }
  let schema: yup.NumberSchema = yup
    .number()
    .transform((_, originalValue) => {
      if (typeof originalValue === 'string' && originalValue === '') return undefined
      if (typeof originalValue === 'string')
        return +originalValue.replaceAll(/\s/g, '').replaceAll(/[.,]/g, '.')
      return originalValue
    })
    .typeError(question.errorMessage)

  if (typeof config.min === 'number' && config.min !== 0)
    schema = schema.min(config.min, question.errorMessage)
  if (typeof config.max === 'number' && config.max !== 0)
    schema = schema.max(config.max, question.errorMessage)
  return schema
}

const buildValueListSchema = (question: QuestionType): yup.StringSchema => {
  const config = question.validatorConfig as { multiple?: boolean }
  if (!config.multiple) {
    return yup.string().oneOf(
      question.availableResponses.filter((resp) => resp.display).map((resp) => resp.value),
      question.errorMessage,
    )
  }
  // Gestion du cas multiple (valeurs renvoyées sous forme de tableau stringifié : "['val1','val2']")
  return yup.string().test('is-array-of-allowed-values', question.errorMessage, (value) => {
    if (!value) return true // Géré par le required plus tard
    try {
      const parsedArray = JSON.parse(value) as string[]
      if (!Array.isArray(parsedArray)) return false
      const allowedValues = new Set(
        question.availableResponses.filter((resp) => resp.display).map((resp) => resp.value),
      )
      return parsedArray.every((val) => allowedValues.has(val))
    } catch {
      return false
    }
  })
}

const buildVehicleSchema = (question: QuestionType): yup.StringSchema =>
  yup
    .string()
    .required(question.errorMessage)
    .test('correct defaultValue', question.errorMessage, (value) => {
      try {
        // sraCode identifie un véhicule auto/VSP, idMoto identifie une moto
        const parsedJson = JSON.parse(value) as VehicleDefaultValue & { idMoto?: string }
        return Boolean(parsedJson.sraCode) || Boolean(parsedJson.idMoto)
      } catch {
        return false
      }
    })

const buildBaseSchema = (
  question: QuestionType,
  validationObject: { [key: string]: yup.Schema },
): void => {
  switch (question.validatorKind) {
    case 'Date':
      validationObject[`date_${question.extraInfo}`] = buildDateSchema(question)
      break
    case 'FreeTextFree':
      validationObject[question.extraInfo] = buildFreeTextFreeSchema(question)
      break
    case 'FreeTextNumber':
      validationObject[question.extraInfo] = buildFreeTextNumberSchema(question)
      break
    case 'FreeTextEmail':
      validationObject[question.extraInfo] = yup
        .string()
        .matches(emailPattern, question.errorMessage)
      break
    case 'FreeTextPhone':
      validationObject[question.extraInfo] = yup
        .string()
        .matches(phonePattern, question.errorMessage)
      break
    case 'ZipCodeCity':
      validationObject[question.extraInfo] = yup
        .string()
        .matches(
          /^\s*\{\s*"cp"\s*:\s*"\d{5}"\s*,\s*"town"\s*:\s*"[a-zA-ZÀ-ÿ\-\s']+"\s*\}\s*$/,
          question.errorMessage,
        )
      break
    case 'AutoComplete':
      validationObject[question.extraInfo] = yup
        .string()
        .matches(
          /^\s*\{\s*"id"\s*:\s*"\d+"\s*,\s*"label"\s*:\s*"[a-zA-ZÀ-ÿ\-\s']+"\s*\}\s*$/,
          question.errorMessage,
        )
      break
    case 'Optin':
      validationObject[question.extraInfo] = yup.string()
      break
    case 'ValueListSelect':
    case 'ValueListButton':
    case 'ValueListRadio':
    case 'ValueListCheckbox':
    case 'ValueListSlider':
      validationObject[question.extraInfo] = buildValueListSchema(question)
      break
    default:
      validationObject[question.extraInfo] = yup.string()
      break
  }
}

const applyRequiredConstraint = (
  question: QuestionType,
  validationObject: { [key: string]: yup.Schema },
): void => {
  if (question.validatorKind === 'Vehicle') {
    validationObject[question.extraInfo] = buildVehicleSchema(question)
    return
  }

  if (question.validatorKind === 'Optin') {
    // L'optin requis exige '1' (coché) ; optionnel = toute valeur acceptée
    if (!question.optional) {
      validationObject[question.extraInfo] = yup.string().oneOf(['1'], question.errorMessage)
    }
    return
  }

  const key = question.validatorKind === 'Date' ? `date_${question.extraInfo}` : question.extraInfo

  if (question.optional) {
    validationObject[key] = validationObject[key]
      .transform((curr: unknown, originalValue: unknown) =>
        typeof originalValue === 'string' && originalValue === '' ? undefined : curr,
      )
      .notRequired()
  } else {
    validationObject[key] = (validationObject[key] as yup.StringSchema).required(
      question.errorMessage,
    )
  }
}

// ----- Fonction principale -----

const handleQuestion = (questions: QuestionType[]): yup.ObjectShape => {
  const validationObject: { [key: string]: yup.Schema } = {}

  questions
    .filter((quest) => quest.display)
    .forEach((question) => {
      buildBaseSchema(question, validationObject)
      applyRequiredConstraint(question, validationObject)
    })

  return validationObject
}

export const handleYupSchema = (
  currentSlide: Slide,
): yup.ObjectSchema<
  {
    [x: string]: unknown
  },
  yup.AnyObject,
  {
    [x: string]: undefined
  },
  ''
> => {
  const questions: QuestionType[] = currentSlide?.groups?.flatMap<QuestionType>((group) =>
    getQuestionFromElements(group.elements),
  )
  return yup.object(handleQuestion(questions))
}

export const handleDefaultValues = (currentSlide: Slide) => {
  const defaultValues: { [key: string]: string } = {}

  currentSlide?.groups
    ?.flatMap<QuestionType>((group) => getQuestionFromElements(group.elements))
    ?.forEach((question: QuestionType) => {
      if (question.validatorKind === 'Date')
        defaultValues[`date_${question.extraInfo}`] = question.response ?? ''
      else if (
        (question.validatorKind === 'ValueListButton' ||
          question.validatorKind === 'ValueListCheckbox' ||
          question.validatorKind === 'ValueListSelect' ||
          question.validatorKind === 'ValueListRadio') &&
        question.availableResponses.filter(
          (resp) =>
            resp.value === question.response || resp.value.startsWith(`${question.response}|`),
        ).length > 1
      ) {
        defaultValues[question.extraInfo] = question.responseLabel
          ? `${question.response}|${question.responseLabel}`
          : (question.response ?? '')
      } else defaultValues[question.extraInfo] = question.response ?? ''
    })

  return defaultValues
}

export interface Response {
  key: string
  response: string
  responseLabel?: string
}

export const transformResponse = (
  responses: Response[],
  currentSlide: Slide | Group,
): Response[] => {
  const questions =
    'groups' in currentSlide
      ? currentSlide?.groups?.flatMap<QuestionType>((group) =>
          getQuestionFromElements(group.elements),
        )
      : getQuestionFromElements(currentSlide.elements)
  const returnedResponses = duplicateObject(responses).map((response) => {
    // Transforme les dates
    if (response.key.startsWith('date_')) {
      // On récupère la clé sans le préfixe date_
      const newKey = response.key.replace('date_', '')

      const question = questions.find((quest) => quest.extraInfo === newKey)

      // On récupère le format de la date
      const jsonConfig = question?.validatorConfig as DateConfig
      const formatDate = getFormatDate(jsonConfig.jour, jsonConfig.mois)
      // On mets à jour la réponse
      response.response = dayjs(response.response).format(formatDate)
      response.key = newKey
      response.responseLabel = question?.availableResponses.find(
        (resp) => resp.value === response.response,
      )?.label
    }

    const question = questions.find((quest) => quest.extraInfo === response.key)

    if (question?.display) {
      // Si ValueListButton ou ValueListSelect, on enregistre aussi le libellé de la réponse
      if (
        (question?.validatorKind === 'ValueListButton' ||
          question?.validatorKind === 'ValueListCheckbox' ||
          question?.validatorKind === 'ValueListSelect' ||
          question?.validatorKind === 'ValueListRadio') &&
        response.response.includes('|')
      ) {
        const splitResponse = response.response.split('|')
        response.response = splitResponse[0]
        response.responseLabel = splitResponse[1]
      }

      // Si FreeText autre que FreeTextNumber, on enlève les espaces à la fin du string
      if (
        question?.validatorKind === 'FreeTextFree' ||
        question?.validatorKind === 'FreeTextEmail' ||
        question?.validatorKind === 'FreeTextPhone'
      ) {
        response.response = response.response.trimEnd()
      }

      // Si FreeTextNumber, on enlève les espaces
      if (question?.validatorKind === 'FreeTextNumber') {
        const replaceStr = response.response.toString().replaceAll(/\s/g, '')
        if (replaceStr !== '') {
          response.response = (
            question.validatorConfig.decimal ? +replaceStr : Math.floor(+replaceStr)
          ).toString()
        }
      }

      // Si Autocomplete, on garde que l'id
      if (question?.validatorKind === 'AutoComplete') {
        response.response = response.response.split('|')[0]
      }
    } else {
      response.response = ''
    }

    return response
  })

  // Gestion de l'optin1Label
  // Lire la valeur depuis le DOM rendu (innerText = texte exact affiché à l'utilisateur)
  const optinGroup = document.getElementById('optin1Group')

  // N'injecter que si :
  // 1. L'élément optin est présent dans le DOM (display: true)
  // 2. La réponse optin fait partie du batch soumis (pas un save inline d'un autre champ)
  if (optinGroup) {
    const hasOptinInBatch = responses.some((r) => r.key === 'OptIn1')

    if (hasOptinInBatch) {
      returnedResponses.push({
        key: 'OptIn1Label',
        response: optinGroup.innerText,
      })
    }
  }

  return returnedResponses
}

export const getNextButtonLabel = (
  currentSlide: Slide,
  lastCtaLabel?: string,
  ctaLabel?: string,
): string => {
  // Bouton de validation final
  if (currentSlide.isLastSlide && lastCtaLabel) return lastCtaLabel

  // Autres boutons de validation
  if (ctaLabel) return ctaLabel

  return 'Valider et continuer'
}

export const isElementStep = (group: Group): group is OneElementGroup => group.elements.length === 1

export const isMessageStep = (group: Group): boolean =>
  isElementStep(group) && group.elements[0].type === 'message'

export const isQuestionStep = (group: Group): boolean =>
  isElementStep(group) && group.elements[0].type === 'question'

/**
 * Retourne true si l'appel à handleBlur/saveResponse doit être ignoré depuis handleChange.
 * Cas 1 : le champ est un ValueListButton sur un slide mono-question (goNextOnSelect=true),
 * ce qui signifie que le clic soumet le formulaire via isSubmitButton.
 * validateSlide/validateForm prend en charge la sauvegarde ; appeler saveResponse
 * en plus provoquerait un double appel saveAndValidate.
 * Cas 2 : le champ est un Optin — la sauvegarde inline n'est pas déclenchée pour ce type.
 */
export function shouldSkipInlineSave(validatorKind: string, goNextOnSelect: boolean): boolean {
  return (goNextOnSelect && validatorKind === 'ValueListButton') || validatorKind === 'Optin'
}

export const getLastQuestionDisplayInSlide = (slide: Slide): QuestionType | undefined => {
  const groupsWithDisplayedQuestions = slide.groups.filter((group) =>
    getQuestionFromElements(group.elements).find((question) => question.display),
  )

  if (groupsWithDisplayedQuestions.length === 0) return undefined

  const lastGroup = groupsWithDisplayedQuestions[groupsWithDisplayedQuestions.length - 1]
  // .findLast() est ES2023, non supporté sur Safari/iOS < 15.4 (MPT-20776) :
  // on reproduit le même comportement via reverse().find()
  return [...getQuestionFromElements(lastGroup.elements)]
    .reverse()
    .find((question) => question.display)
}
